import { localize } from '@saviynt/common';
import { useIntl } from 'react-intl';

import type { SharedMessage } from '../shared/messages';

export type Primitive = string | number | boolean | null | undefined;

export const useLocalize = () => {
  const intl = useIntl();

  return (
    key: string | { id: string; defaultMessage: string },
    placeholders?: Record<string, Primitive>
  ): string => localize(intl, key, placeholders);
};

export type InvertedLocalize<M extends string> = (
  defaultMessage: M,
  placeholders?: Record<string, Primitive>
) => string;

// helper type to prevent component-specific keys from duplicating shared message keys
type NoDuplicateSharedKeys<C extends string> = {
  [K in C]: K extends SharedMessage ? never : string;
};

/**
 * Merges shared messages with component-specific messages
 * Prevents component messages from duplicating shared message keys
 *
 * IMPORTANT: This function enforces the duplicate prevention constraint.
 * Files that use the spread pattern ({ ...sharedMessages, ...componentMessages })
 * bypass this constraint and won't be protected from duplicate keys.
 * Always use mergeInvertedMessages() instead of spreading directly.
 *
 * @param shared - Shared messages object (allowed keys)
 * @param component - Component-specific messages (duplicate shared keys are blocked)
 * @returns Merged messages object
 */
export function mergeInvertedMessages<C extends string>(
  shared: Record<SharedMessage, string>,
  component: NoDuplicateSharedKeys<C>
): Record<SharedMessage | C, string> {
  return { ...shared, ...component } as Record<SharedMessage | C, string>;
}

export const useInvertedLocalize = <M extends string>(
  invertedLookup: Record<M, string>
): InvertedLocalize<M> => {
  const intl = useIntl();

  return (
    defaultMessage: M,
    placeholders?: Record<string, Primitive>
  ): string => {
    const id = invertedLookup[defaultMessage];

    if (!id) {
      return defaultMessage;
    }

    return localize(intl, { id, defaultMessage }, placeholders);
  };
};
